"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ShieldCheck, MapPin, Droplets, FileCheck, ArrowRight, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const trustPoints = [{
  icon: MapPin,
  title: "Miami-Dade & Broward Coverage",
  description: "Extensive local knowledge of South Florida's unique construction and climate."
}, {
  icon: ShieldCheck,
  title: "Licensed Florida Inspectors",
  description: "Fully certified professionals adhering to strict InterNACHI and state standards."
}, {
  icon: Droplets,
  title: "Specialized Moisture Detection",
  description: "Advanced thermal imaging and moisture meters for tropical humidity challenges."
}, {
  icon: FileCheck,
  title: "Insurance Inspection Options",
  description: "Wind mitigation and 4-Point inspections are available for Florida insurance documentation."
}];
export default function BurntStoreLocalExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="249540" id="service-areas" className="w-full py-16 md:py-24 bg-background text-foreground overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Card className="border-border shadow-xl overflow-hidden rounded-xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Content Column */}
              <CardContent className="p-8 md:p-12 lg:p-16 flex flex-col justify-center bg-card">
                <div className="space-y-6">
                  <Badge variant="secondary" className="mb-2 bg-primary/10 text-primary border-none">
                    <MapPin className="w-3 h-3 mr-1" />
                    South Florida Coverage
                  </Badge>
                  
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight text-foreground">
                    Your Local <span className="text-primary">Miami & Homestead</span> Inspection Experts
                  </h2>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed font-default">
                    Navigating the real estate market in <strong className="text-foreground font-semibold">Miami-Dade</strong> requires an inspector who understands local building codes and environmental factors. From historic homes in Coral Gables to new builds in Homestead, we provide the clarity you need.
                  </p>
                  
                  <p className="text-lg text-muted-foreground leading-relaxed font-default">
                    As a premier inspection firm, we serve <strong className="text-foreground font-semibold">South Florida</strong> families with detailed reports and professional insights tailored to our unique subtropical landscape and high-velocity hurricane zones.
                  </p>

                  <Separator className="my-8 bg-border" />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {trustPoints.map((point, i) => <div data-index={i} key={i} className="flex items-start gap-3">
                        <div className="mt-1 bg-primary/10 p-2 rounded-md shrink-0">
                          <point.icon className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-semibold font-default text-foreground">{point.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1 font-default">{point.description}</p>
                        </div>
                      </div>)}
                  </div>

                  <div className="pt-6">
                    <Button asChild size="lg" className="w-full sm:w-auto group bg-primary hover:bg-primary/90 text-primary-foreground rounded-md">
                      <Link to="https://trusted-fl.com/#schedule-your-inspection">
                        Schedule Your Inspection
                        <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>

              {/* Image Column */}
              <div className="relative min-h-[400px] lg:min-h-full bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/miami_neighborhood_skyline.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/miami_neighborhood_skyline.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/miami_neighborhood_skyline.png-wvc-sizes" alt="Aerial view of Miami residential neighborhoods and skyline" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent lg:bg-gradient-to-l lg:from-transparent lg:via-background/20 lg:to-background/40" />
                
                {/* Floating Badge */}
                <div className="absolute bottom-8 left-8 right-8 md:left-auto md:right-8 md:bottom-8">
                  <Card className="bg-card/95 backdrop-blur-md border-border shadow-2xl rounded-lg">
                    <CardContent className="p-6 flex items-center gap-4">
                      <div className="bg-primary text-primary-foreground p-3 rounded-full shrink-0 shadow-lg">
                        <ShieldCheck className="w-8 h-8" />
                      </div>
                      <div>
                        <p className="font-bold font-default text-lg text-foreground">Certified & Insured</p>
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1 font-default">
                          <CircleCheck className="w-4 h-4 text-primary" />
                          InterNACHI Verified Professional
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>;
}